/**
 * Missing Stock Intelligence - Analytical Logic Layer
 * 
 * This module provides analytical calculations for detecting potential stock deviations.
 * Each analysis module (Snapshot, Growth vs Model, Spreadsheet Upload) uses this layer
 * independently while sharing common calculation utilities.
 * 
 * Currently calibrated for Seabream. Other species will be added in future releases.
 */

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

// Seabream biological parameters (baseline for all calculations)
export const SEABREAM_PARAMS = {
  expectedFcrRange: { min: 1.4, max: 1.8 },  // Normal FCR range
  expectedSgrRange: { min: 0.5, max: 2.5 },  // Normal SGR range (% per day)
  maxBiologicalGrowthRate: 3.5,  // Max realistic daily growth (%)
  samplingMarginOfError: 0.05,   // 5% sampling margin
  minSamplesForReliability: 3,
  optimalSamplingIntervalDays: 14,
  maxAcceptableSamplingGapDays: 30,
};

// Signal strength thresholds
export const SIGNAL_STRENGTH = {
  WEAK: { min: 0, max: 0.05, label: 'Weak' },
  MODERATE: { min: 0.05, max: 0.10, label: 'Moderate' },
  STRONG: { min: 0.10, max: Infinity, label: 'Strong' },
};

// ============================================================================
// SNAPSHOT ANALYSIS - LIGHTWEIGHT CALCULATIONS
// ============================================================================

/**
 * Snapshot Analysis: Classify interpretation based on FCR deviation
 * @param {number} observedFcr - Observed FCR value
 * @param {number} expectedFcr - Expected/model FCR value
 * @returns {Object} Classification result
 */
export const classifySnapshotDeviation = (observedFcr, expectedFcr) => {
  const deviation = (observedFcr - expectedFcr) / expectedFcr;
  const absDeviation = Math.abs(deviation);
  
  // Determine signal strength
  let signalStrength;
  if (absDeviation <= SIGNAL_STRENGTH.WEAK.max) {
    signalStrength = 'Weak';
  } else if (absDeviation <= SIGNAL_STRENGTH.MODERATE.max) {
    signalStrength = 'Moderate';
  } else {
    signalStrength = 'Strong';
  }
  
  let classification;
  
  if (absDeviation <= 0.05) {
    classification = 'Sampling uncertainty';
  } else if (deviation > 0) {
    if (absDeviation > 0.15) {
      classification = 'Possible stock deviation';
    } else if (absDeviation > 0.10) {
      classification = 'Possible feeding inefficiency';
    } else {
      classification = 'Possible biological underperformance';
    }
  } else {
    if (absDeviation > 0.10) {
      classification = 'Possible stock deviation';
    } else {
      classification = 'Possible biological outperformance';
    }
  }
  
  return {
    deviation: Math.round(deviation * 1000) / 10,
    absDeviation: Math.round(absDeviation * 1000) / 10,
    signalStrength,
    classification,
  };
};

// ============================================================================
// GROWTH VS MODEL - ANALYTICAL SIGNALS
// ============================================================================

/**
 * Calculate deviation pattern classification
 * @param {Array} deviations - Array of {day, deviation_percent} objects sorted by day
 * @returns {Object} Pattern classification
 */
export const classifyDeviationPattern = (deviations, overallTrend = '') => {
  if (!deviations || deviations.length < 2) {
    return {
      pattern: 'Insufficient data',
    };
  }
  
  const changes = [];
  for (let i = 1; i < deviations.length; i++) {
    changes.push(deviations[i].deviation_percent - deviations[i - 1].deviation_percent);
  }
  
  const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
  const variance = changes.reduce((sum, c) => sum + Math.pow(c - avgChange, 2), 0) / changes.length;
  const volatility = Math.sqrt(variance);
  
  // Count sign changes
  let signChanges = 0;
  for (let i = 1; i < changes.length; i++) {
    if ((changes[i] > 0 && changes[i - 1] < 0) || (changes[i] < 0 && changes[i - 1] > 0)) {
      signChanges++;
    }
  }
  
  // Check for sudden shift (large single change)
  const maxAbsChange = Math.max(...changes.map(Math.abs));
  const suddenShiftThreshold = 20; // 20% change in single interval
  
  // Determine if trend is below model for description consistency
  // Use both the trend string AND actual deviation data to avoid misclassification
  const belowSamples = deviations.filter(d => d.deviation_percent < -5).length;
  const aboveSamples = deviations.filter(d => d.deviation_percent > 5).length;
  const isBelowTrend = (overallTrend && (
    overallTrend.includes('below') || overallTrend.includes('Below')
  )) || (belowSamples > aboveSamples);
  
  let pattern;
  
  if (maxAbsChange > suddenShiftThreshold && changes.length > 1) {
    const maxChangeIdx = changes.reduce((bestIdx, c, i) => Math.abs(c) > Math.abs(changes[bestIdx]) ? i : bestIdx, 0);
    const maxChangeValue = changes[maxChangeIdx];
    const deviationAfterShift = deviations[maxChangeIdx + 1]?.deviation_percent ?? 0;

    if (maxChangeValue > 0 && deviationAfterShift > 0 && !isBelowTrend) {
      pattern = 'Sudden upward shift';
    } else {
      pattern = 'No upward shift detected';
    }
  } else if (signChanges >= changes.length * 0.5 && volatility > 10) {
    pattern = 'Oscillating deviation';
  } else if (Math.abs(avgChange) < 3 && volatility < 8) {
    if (Math.abs(deviations[deviations.length - 1].deviation_percent) < 5) {
      pattern = 'Stable alignment';
    } else {
      pattern = 'Stable deviation';
    }
  } else if (avgChange > 2 && !isBelowTrend) {
    pattern = 'Gradual divergence';
  } else if (avgChange < -2 || (avgChange > 2 && isBelowTrend)) {
    pattern = 'Gradual convergence';
  } else {
    pattern = 'Mixed pattern';
  }
  
  return {
    pattern,
    avgChange: Math.round(avgChange * 10) / 10,
    volatility: Math.round(volatility * 10) / 10,
    signChanges,
  };
};

/**
 * Calculate deviation persistence streak (any direction — absolute deviation > margin)
 * @param {Array} deviations - Array of {deviation_percent} objects in chronological order
 * @returns {Object} Streak analysis
 */
export const calculatePositiveStreak = (deviations) => {
  if (!deviations || deviations.length === 0) {
    return { currentStreak: 0, maxStreak: 0, streakStartIndex: -1 };
  }
  
  const threshold = SEABREAM_PARAMS.samplingMarginOfError * 100;
  let currentStreak = 0;
  let maxStreak = 0;
  let maxStreakStartIndex = -1;
  let currentStreakStartIndex = -1;
  
  for (let i = 0; i < deviations.length; i++) {
    if (Math.abs(deviations[i].deviation_percent) > threshold) {
      if (currentStreak === 0) {
        currentStreakStartIndex = i;
      }
      currentStreak++;
      if (currentStreak > maxStreak) {
        maxStreak = currentStreak;
        maxStreakStartIndex = currentStreakStartIndex;
      }
    } else {
      currentStreak = 0;
    }
  }
  
  const activeStreak = currentStreak > 0 ? currentStreak : 0;
  
  return {
    currentStreak: activeStreak,
    maxStreak,
    streakStartIndex: maxStreakStartIndex,
    isActiveStreak: currentStreak > 0,
  };
};

/**
 * Calculate deviation stability (volatility measure)
 * @param {Array} deviations - Array of {deviation_percent} objects
 * @returns {Object} Stability analysis
 */
export const calculateDeviationStability = (deviations) => {
  if (!deviations || deviations.length < 2) {
    return { volatility: 0, stability: 'Insufficient data' };
  }
  
  const values = deviations.map(d => d.deviation_percent);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);
  
  const cv = mean !== 0 ? Math.abs(stdDev / mean) : stdDev;
  
  let stability;
  
  if (stdDev < 5) {
    stability = 'Very stable';
  } else if (stdDev < 10) {
    stability = 'Stable';
  } else if (stdDev < 20) {
    stability = 'Moderate volatility';
  } else {
    stability = 'High volatility';
  }
  
  return {
    volatility: Math.round(stdDev * 10) / 10,
    coefficientOfVariation: Math.round(cv * 100) / 100,
    stability,
    mean: Math.round(mean * 10) / 10,
  };
};

/**
 * Detect earliest meaningful divergence
 * @param {Array} deviations - Array of {day, deviation_percent, sample_date} objects
 * @param {number} threshold - Minimum deviation to consider meaningful (default 5%)
 * @returns {Object} Divergence detection result
 */
export const detectEarliestDivergence = (deviations, threshold = 5) => {
  if (!deviations || deviations.length === 0) {
    return { detected: false };
  }
  
  for (let i = 0; i < deviations.length; i++) {
    if (Math.abs(deviations[i].deviation_percent) > threshold) {
      const isPositive = deviations[i].deviation_percent > 0;
      let persists = i === deviations.length - 1;
      
      for (let j = i + 1; j < deviations.length; j++) {
        if ((isPositive && deviations[j].deviation_percent > 0) || 
            (!isPositive && deviations[j].deviation_percent < 0)) {
          persists = true;
          break;
        }
      }
      
      if (persists) {
        return {
          detected: true,
          date: deviations[i].sample_date,
          day: deviations[i].days_since_arrival || deviations[i].day,
          deviation: deviations[i].deviation_percent,
          direction: isPositive ? 'positive' : 'negative',
        };
      }
    }
  }
  
  return {
    detected: false,
  };
};

/**
 * Calculate growth signal strength
 * @param {Object} params - Analysis parameters
 * @returns {Object} Signal strength assessment
 */
export const calculateGrowthSignalStrength = (deviations, streak, stability) => {
  if (!deviations || deviations.length === 0) {
    return { strength: 'Insufficient data', score: 0 };
  }
  
  let score = 0;
  
  // Magnitude factor (0-35 points)
  const avgDeviation = deviations.reduce((sum, d) => sum + Math.abs(d.deviation_percent), 0) / deviations.length;
  score += Math.min(avgDeviation * 2, 35);
  
  // Persistence factor (0-35 points)
  if (streak && streak.maxStreak) {
    score += Math.min(streak.maxStreak * 10, 35);
  }
  
  // Stability factor (0-30 points) - Higher stability = higher score
  if (stability && stability.volatility !== undefined) {
    if (stability.volatility < 5) score += 30;
    else if (stability.volatility < 10) score += 20;
    else if (stability.volatility < 20) score += 10;
    else score += 5;
  }
  
  score = Math.min(Math.round(score), 100);
  
  let strength;
  if (score >= 70) strength = 'Strong';
  else if (score >= 40) strength = 'Moderate';
  else strength = 'Weak';
  
  return { strength, score };
};

// ★ generateBiologicalInterpretation DELETED — all narrative from ssotOverride.js only

// ============================================================================
// SPREADSHEET UPLOAD - HISTORICAL ANALYSIS
// ============================================================================

/**
 * Find the strongest deviation window in historical data
 * @param {Array} deviations - Array of {day, deviation_percent, sample_date} objects
 * @param {number} windowSize - Number of samples to consider (default 3)
 * @returns {Object} Strongest deviation window
 */
export const findStrongestDeviationWindow = (deviations, windowSize = 3) => {
  if (!deviations || deviations.length < windowSize) {
    return { found: false };
  }
  
  let maxAbsDeviation = -Infinity;
  let strongestWindow = null;
  
  for (let i = 0; i <= deviations.length - windowSize; i++) {
    const window = deviations.slice(i, i + windowSize);
    const avgDev = window.reduce((sum, d) => sum + d.deviation_percent, 0) / windowSize;
    
    if (Math.abs(avgDev) > maxAbsDeviation) {
      maxAbsDeviation = Math.abs(avgDev);
      strongestWindow = {
        startIndex: i,
        endIndex: i + windowSize - 1,
        startDate: window[0].sample_date,
        endDate: window[windowSize - 1].sample_date,
        avgDeviation: Math.round(avgDev * 10) / 10,
        samples: window,
      };
    }
  }
  
  return {
    found: true,
    ...strongestWindow,
  };
};

/**
 * Calculate analytical strength score
 * @param {Array} samples - Array of sample data
 * @param {Date|string} startDate - Start date of analysis period
 * @param {Date|string} endDate - End date of analysis period
 * @returns {Object} Analytical strength assessment
 */
export const calculateAnalyticalStrength = (samples) => {
  if (!samples || samples.length === 0) {
    return { score: 0, breakdown: {} };
  }
  
  const breakdown = {
    sampleCount: { value: samples.length, maxScore: 30, score: 0 },
    timeSpan: { value: 0, maxScore: 25, score: 0 },
    weightRange: { value: 0, maxScore: 25, score: 0 },
    intervalConsistency: { value: 0, maxScore: 20, score: 0 },
  };
  
  // Sample count score (0-30)
  if (samples.length >= 10) breakdown.sampleCount.score = 30;
  else if (samples.length >= 5) breakdown.sampleCount.score = 20;
  else if (samples.length >= 3) breakdown.sampleCount.score = 10;
  else breakdown.sampleCount.score = 5;
  
  // Time span score (0-25)
  if (samples.length >= 2) {
    const dates = samples.map(s => new Date(s.sample_date || s.date)).sort((a, b) => a - b);
    const timeSpanDays = (dates[dates.length - 1] - dates[0]) / (1000 * 60 * 60 * 24);
    breakdown.timeSpan.value = Math.round(timeSpanDays);
    
    if (timeSpanDays >= 90) breakdown.timeSpan.score = 25;
    else if (timeSpanDays >= 60) breakdown.timeSpan.score = 20;
    else if (timeSpanDays >= 30) breakdown.timeSpan.score = 15;
    else breakdown.timeSpan.score = 5;
  }
  
  // Weight range score (0-25)
  const weights = samples.map(s => parseFloat(s.actual_weight_g || s.weight)).filter(w => !isNaN(w));
  if (weights.length >= 2) {
    const minWeight = Math.min(...weights);
    const maxWeight = Math.max(...weights);
    const range = maxWeight - minWeight;
    breakdown.weightRange.value = Math.round(range);
    
    if (range >= 100) breakdown.weightRange.score = 25;
    else if (range >= 50) breakdown.weightRange.score = 20;
    else if (range >= 20) breakdown.weightRange.score = 15;
    else breakdown.weightRange.score = 5;
  }
  
  // Interval consistency score (0-20)
  if (samples.length >= 3) {
    const dates = samples.map(s => new Date(s.sample_date || s.date)).sort((a, b) => a - b);
    const intervals = [];
    for (let i = 1; i < dates.length; i++) {
      intervals.push((dates[i] - dates[i - 1]) / (1000 * 60 * 60 * 24));
    }
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const intervalVariance = intervals.reduce((sum, i) => sum + Math.pow(i - avgInterval, 2), 0) / intervals.length;
    const intervalStdDev = Math.sqrt(intervalVariance);
    breakdown.intervalConsistency.value = Math.round(intervalStdDev);
    
    if (intervalStdDev < 5) breakdown.intervalConsistency.score = 20;
    else if (intervalStdDev < 10) breakdown.intervalConsistency.score = 15;
    else if (intervalStdDev < 20) breakdown.intervalConsistency.score = 10;
    else breakdown.intervalConsistency.score = 5;
  }
  
  const totalScore = Object.values(breakdown).reduce((sum, b) => sum + b.score, 0);
  
  return {
    score: totalScore,
    breakdown,
  };
};

/**
 * Detect data quality warnings
 * @param {Array} samples - Array of sample data with dates and weights
 * @returns {Array} Array of warning objects
 */
export const detectDataQualityWarnings = (samples) => {
  const warnings = [];
  
  if (!samples || samples.length === 0) {
    warnings.push({
      type: 'critical',
      code: 'NO_SAMPLES',
      message: 'No sample data available',
    });
    return warnings;
  }
  
  // Check sample count
  if (samples.length < SEABREAM_PARAMS.minSamplesForReliability) {
    warnings.push({
      type: 'warning',
      code: 'INSUFFICIENT_SAMPLES',
      message: `Only ${samples.length} samples available. At least ${SEABREAM_PARAMS.minSamplesForReliability} recommended for reliable analysis.`,
    });
  }
  
  // Sort by date for temporal analysis
  const sortedSamples = [...samples].sort((a, b) => {
    const dateA = new Date(a.sample_date || a.date);
    const dateB = new Date(b.sample_date || b.date);
    return dateA - dateB;
  });
  
  // Check for large sampling gaps
  for (let i = 1; i < sortedSamples.length; i++) {
    const prevDate = new Date(sortedSamples[i - 1].sample_date || sortedSamples[i - 1].date);
    const currDate = new Date(sortedSamples[i].sample_date || sortedSamples[i].date);
    const gapDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);
    
    if (gapDays > SEABREAM_PARAMS.maxAcceptableSamplingGapDays) {
      warnings.push({
        type: 'warning',
        code: 'LARGE_SAMPLING_GAP',
        message: `${Math.round(gapDays)}-day gap between samples reduces reliability.`,
        details: { from: sortedSamples[i - 1].sample_date, to: sortedSamples[i].sample_date, gap: Math.round(gapDays) },
      });
    }
  }
  
  // Check for unrealistic weight jumps
  for (let i = 1; i < sortedSamples.length; i++) {
    const prevWeight = parseFloat(sortedSamples[i - 1].actual_weight_g || sortedSamples[i - 1].weight);
    const currWeight = parseFloat(sortedSamples[i].actual_weight_g || sortedSamples[i].weight);
    const prevDate = new Date(sortedSamples[i - 1].sample_date || sortedSamples[i - 1].date);
    const currDate = new Date(sortedSamples[i].sample_date || sortedSamples[i].date);
    const daysBetween = Math.max(1, (currDate - prevDate) / (1000 * 60 * 60 * 24));
    
    // Calculate daily growth rate
    const dailyGrowthRate = ((currWeight - prevWeight) / prevWeight / daysBetween) * 100;
    
    if (dailyGrowthRate > SEABREAM_PARAMS.maxBiologicalGrowthRate) {
      warnings.push({
        type: 'warning',
        code: 'UNREALISTIC_WEIGHT_JUMP',
        message: `Growth rate ${dailyGrowthRate.toFixed(1)}%/day exceeds biological threshold`,
        details: { from: prevWeight, to: currWeight, dailyRate: dailyGrowthRate.toFixed(2) },
      });
    }
    
    if (currWeight < prevWeight * 0.9) {
      warnings.push({
        type: 'warning',
        code: 'WEIGHT_DECREASE',
        message: `Weight decrease: ${prevWeight.toFixed(1)}g to ${currWeight.toFixed(1)}g`,
        details: { from: prevWeight, to: currWeight },
      });
    }
  }
  
  return warnings;
};

// ============================================================================
// BIOMASS-DENSITY ESTIMATOR (Advanced Analysis)
// ============================================================================

/**
 * Estimate biomass reduction required to explain growth acceleration
 * Only activated for Growth vs Model and Spreadsheet Upload when conditions are met
 * 
 * @param {Array} deviations - Array of deviation data
 * @param {Object} stability - Stability analysis result
 * @param {number} initialFishCount - Initial fish count (optional, for fish count estimate)
 * @returns {Object} Biomass-density estimate
 */
export const estimateBiomassDensity = (deviations, stability, initialFishCount = null, overallTrend = '') => {
  // If overall trend is below model, biomass density reduction is not applicable
  // Use both trend string and actual data
  const belowCount = deviations ? deviations.filter(d => d.deviation_percent < -5).length : 0;
  const aboveCount = deviations ? deviations.filter(d => d.deviation_percent > 5).length : 0;
  const isBelowTrend = (overallTrend && (
    overallTrend.includes('below') || overallTrend.includes('Below')
  )) || (belowCount > aboveCount);

  if (isBelowTrend) {
    return {
      available: false,
      reason: 'Overall trend is below model; reduced density signal not detected',
    };
  }

  // Activation conditions check
  if (!deviations || deviations.length < 2) {
    return {
      available: false,
      reason: 'At least 2-3 valid samples required',
    };
  }
  
  // Calculate average positive deviation
  const positiveDeviations = deviations.filter(d => d.deviation_percent > SEABREAM_PARAMS.samplingMarginOfError * 100);
  
  if (positiveDeviations.length < 2) {
    return {
      available: false,
      reason: 'Positive deviation does not exceed sampling margin persistently',
    };
  }
  
  // Check stability
  if (stability && stability.stability === 'High volatility') {
    return {
      available: false,
      reason: 'Deviation volatility too high for reliable estimate',
    };
  }
  
  const avgPositiveDeviation = positiveDeviations.reduce((sum, d) => sum + d.deviation_percent, 0) / positiveDeviations.length;
  
  // Estimate biomass reduction range
  // Based on biological principle: lower density -> faster individual growth
  // Conservative estimate: 0.5x to 1.2x of deviation percentage
  const lowerBound = Math.max(0, avgPositiveDeviation * 0.5);
  const upperBound = Math.min(30, avgPositiveDeviation * 1.2); // Cap at 30%
  
  // Calculate signal strength for this estimate
  let signalStrength;
  if (avgPositiveDeviation >= 15 && positiveDeviations.length >= 3) {
    signalStrength = 'Strong';
  } else if (avgPositiveDeviation >= 8 && positiveDeviations.length >= 2) {
    signalStrength = 'Moderate';
  } else {
    signalStrength = 'Weak';
  }
  
  const result = {
    available: true,
    estimatedReductionRange: {
      low: Math.round(lowerBound * 10) / 10,
      high: Math.round(upperBound * 10) / 10,
    },
    signalStrength,
    avgPositiveDeviation: Math.round(avgPositiveDeviation * 10) / 10,
    sampleCount: positiveDeviations.length,
  };
  
  if (initialFishCount && initialFishCount > 0) {
    const lowFishReduction = Math.round(initialFishCount * (lowerBound / 100));
    const highFishReduction = Math.round(initialFishCount * (upperBound / 100));
    result.estimatedFishReduction = {
      low: lowFishReduction,
      high: highFishReduction,
    };
  }
  
  return result;
};

// ============================================================================
// SAMPLE RELIABILITY DETECTOR
// ============================================================================

/**
 * Comprehensive sample reliability evaluation
 * Applied to Growth vs Model and Spreadsheet Upload
 * 
 * @param {Array} samples - Sample data array
 * @param {Array} deviations - Deviation analysis array
 * @returns {Object} Reliability assessment
 */
export const evaluateSampleReliability = (samples, deviations) => {
  const checks = {
    sampleCount: { status: 'unknown', impact: 'unknown', score: 0 },
    intervalConsistency: { status: 'unknown', impact: 'unknown', score: 0 },
    volatilityImpact: { status: 'unknown', impact: 'unknown', score: 0 },
    outliers: { status: 'unknown', impact: 'unknown', count: 0, score: 0 },
    weightRange: { status: 'unknown', impact: 'unknown', score: 0 },
    unrealisticJumps: { status: 'unknown', impact: 'unknown', count: 0, score: 0 },
  };
  
  if (!samples || samples.length === 0) {
    return {
      score: 0,
      checks,
    };
  }
  
  // Sample count check (0-20)
  if (samples.length >= 5) {
    checks.sampleCount = { status: 'Good', impact: 'Low', score: 20 };
  } else if (samples.length >= 3) {
    checks.sampleCount = { status: 'Acceptable', impact: 'Moderate', score: 15 };
  } else {
    checks.sampleCount = { status: 'Low', impact: 'High', score: 5 };
  }
  checks.sampleCount.value = samples.length;
  
  // Interval consistency check (0-20)
  if (samples.length >= 2) {
    const dates = samples.map(s => new Date(s.sample_date || s.date)).sort((a, b) => a - b);
    const intervals = [];
    for (let i = 1; i < dates.length; i++) {
      intervals.push((dates[i] - dates[i - 1]) / (1000 * 60 * 60 * 24));
    }
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const variance = intervals.reduce((sum, i) => sum + Math.pow(i - avgInterval, 2), 0) / intervals.length;
    const stdDev = Math.sqrt(variance);
    
    if (stdDev < 5) {
      checks.intervalConsistency = { status: 'Good', impact: 'Low', score: 20 };
    } else if (stdDev < 15) {
      checks.intervalConsistency = { status: 'Acceptable', impact: 'Moderate', score: 15 };
    } else {
      checks.intervalConsistency = { status: 'Poor', impact: 'High', score: 5 };
    }
  }
  
  // Volatility impact (0-20)
  if (deviations && deviations.length >= 2) {
    const devValues = deviations.map(d => d.deviation_percent);
    const mean = devValues.reduce((a, b) => a + b, 0) / devValues.length;
    const variance = devValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / devValues.length;
    const stdDev = Math.sqrt(variance);
    
    if (stdDev < 10) {
      checks.volatilityImpact = { status: 'Low', impact: 'Low', score: 20 };
    } else if (stdDev < 20) {
      checks.volatilityImpact = { status: 'Moderate', impact: 'Moderate', score: 15 };
    } else {
      checks.volatilityImpact = { status: 'High', impact: 'High', score: 5 };
    }
  }
  
  // Outlier detection (0-20)
  if (deviations && deviations.length >= 3) {
    const devValues = deviations.map(d => d.deviation_percent);
    const mean = devValues.reduce((a, b) => a + b, 0) / devValues.length;
    const stdDev = Math.sqrt(devValues.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / devValues.length);
    
    const outlierCount = devValues.filter(v => Math.abs(v - mean) > 2 * stdDev).length;
    checks.outliers.count = outlierCount;
    
    if (outlierCount === 0) {
      checks.outliers = { ...checks.outliers, status: 'None', impact: 'Low', score: 20 };
    } else if (outlierCount === 1) {
      checks.outliers = { ...checks.outliers, status: `${outlierCount} detected`, impact: 'Moderate', score: 12 };
    } else {
      checks.outliers = { ...checks.outliers, status: `${outlierCount} detected`, impact: 'High', score: 5 };
    }
  }
  
  // Weight range suitability (0-10)
  const weights = samples.map(s => parseFloat(s.actual_weight_g || s.weight)).filter(w => !isNaN(w));
  if (weights.length >= 2) {
    const minWeight = Math.min(...weights);
    const maxWeight = Math.max(...weights);
    
    // Check if weight range is suitable for seabream analysis (typically 5g to 500g)
    if (minWeight >= 5 && maxWeight <= 600 && maxWeight > minWeight) {
      checks.weightRange = { status: 'Suitable', impact: 'Low', score: 10, range: `${minWeight.toFixed(0)}-${maxWeight.toFixed(0)}g` };
    } else {
      checks.weightRange = { status: 'Borderline', impact: 'Moderate', score: 5, range: `${minWeight.toFixed(0)}-${maxWeight.toFixed(0)}g` };
    }
  }
  
  // Unrealistic jumps (0-10)
  const warnings = detectDataQualityWarnings(samples);
  const jumpWarnings = warnings.filter(w => w.code === 'UNREALISTIC_WEIGHT_JUMP' || w.code === 'WEIGHT_DECREASE');
  checks.unrealisticJumps.count = jumpWarnings.length;
  
  if (jumpWarnings.length === 0) {
    checks.unrealisticJumps = { ...checks.unrealisticJumps, status: 'None', impact: 'Low', score: 10 };
  } else if (jumpWarnings.length === 1) {
    checks.unrealisticJumps = { ...checks.unrealisticJumps, status: `${jumpWarnings.length} detected`, impact: 'Moderate', score: 5 };
  } else {
    checks.unrealisticJumps = { ...checks.unrealisticJumps, status: `${jumpWarnings.length} detected`, impact: 'High', score: 0 };
  }
  
  // Calculate total score
  const totalScore = Object.values(checks).reduce((sum, c) => sum + (c.score || 0), 0);
  
  return {
    score: totalScore,
    checks,
  };
};

// ============================================================================
// COMBINED ANALYSIS RUNNER (Single-pass efficiency)
// ============================================================================

/**
 * Run complete analytical analysis in a single pass
 * Maximizes efficiency by computing all metrics together
 * 
 * @param {Array} comparisons - Array of comparison data with actual_weight_g, model_weight_g, sample_date
 * @param {string} moduleType - 'growth-model' | 'spreadsheet'
 * @param {number} initialFishCount - Optional initial fish count for biomass estimate
 * @returns {Object} Complete analysis results
 */
export const runCompleteAnalysis = (comparisons, moduleType, initialFishCount = null) => {
  if (!comparisons || comparisons.length === 0) {
    return {
      available: false,
      message: 'No comparison data available for analysis',
    };
  }
  
  // Transform comparisons to deviations (single pass)
  const deviations = comparisons.map((comp, index) => {
    const actualWeight = parseFloat(comp.actual_weight_g || comp.weight);
    const modelWeight = parseFloat(comp.model_weight_g);
    const deviationPercent = modelWeight > 0 ? ((actualWeight - modelWeight) / modelWeight) * 100 : 0;
    
    return {
      sample_number: comp.sample_number || index + 1,
      sample_date: comp.sample_date || comp.date,
      days_since_arrival: comp.days_since_arrival || comp.day,
      actual_weight_g: actualWeight,
      model_weight_g: modelWeight,
      deviation_percent: Math.round(deviationPercent * 100) / 100,
    };
  });
  
  // Calculate all metrics in efficient order (reusing computed values)
  // Determine overall trend direction for signal consistency
  const avgDev = deviations.reduce((s, d) => s + d.deviation_percent, 0) / deviations.length;
  const belowCount = deviations.filter(d => d.deviation_percent < -5).length;
  const aboveCount = deviations.filter(d => d.deviation_percent > 5).length;
  const overallTrend = belowCount > aboveCount ? 'Trending below model' : aboveCount > belowCount ? 'Trending above model' : 'In line with model';

  const pattern = classifyDeviationPattern(deviations, overallTrend);
  const streak = calculatePositiveStreak(deviations);
  const stability = calculateDeviationStability(deviations);
  const earliestDivergence = detectEarliestDivergence(deviations);
  const signalStrength = calculateGrowthSignalStrength(deviations, streak, stability);
  
  // Module-specific analyses
  let spreadsheetAnalysis = null;
  let biomassDensity = null;
  let sampleReliability = null;
  
  if (moduleType === 'spreadsheet') {
    const strongestWindow = findStrongestDeviationWindow(deviations);
    const analyticalStrength = calculateAnalyticalStrength(comparisons);
    const dataWarnings = detectDataQualityWarnings(comparisons);
    
    spreadsheetAnalysis = {
      strongestWindow,
      analyticalStrength,
      dataWarnings,
    };
  }
  
  // Biomass-density estimate (only for growth-model and spreadsheet)
  if (moduleType === 'growth-model' || moduleType === 'spreadsheet') {
    biomassDensity = estimateBiomassDensity(deviations, stability, initialFishCount, overallTrend);
    sampleReliability = evaluateSampleReliability(comparisons, deviations);
  }
  
  return {
    available: true,
    moduleType,
    deviations,
    pattern,
    streak: {
      currentStreak: streak.currentStreak,
      maxStreak: streak.maxStreak,
      isActiveStreak: streak.isActiveStreak,
    },
    stability,
    earliestDivergence,
    signalStrength,
    biomassDensity,
    sampleReliability,
    spreadsheetAnalysis,
    stats: {
      totalSamples: deviations.length,
      avgDeviation: Math.round(
        (deviations.reduce((s, d) => s + d.deviation_percent, 0) / Math.max(1, deviations.length)) * 10
      ) / 10,
      maxDeviation: Math.round(Math.max(...deviations.map(d => d.deviation_percent)) * 10) / 10,
      minDeviation: Math.round(Math.min(...deviations.map(d => d.deviation_percent)) * 10) / 10,
    },
  };
};

export default {
  // Snapshot Analysis
  classifySnapshotDeviation,
  
  // Growth vs Model
  classifyDeviationPattern,
  calculatePositiveStreak,
  calculateDeviationStability,
  detectEarliestDivergence,
  calculateGrowthSignalStrength,
  
  // Spreadsheet Upload
  findStrongestDeviationWindow,
  calculateAnalyticalStrength,
  detectDataQualityWarnings,
  
  // Advanced Analysis
  estimateBiomassDensity,
  evaluateSampleReliability,
  
  // Combined Runner
  runCompleteAnalysis,
  
  // Constants
  SEABREAM_PARAMS,
  SIGNAL_STRENGTH,
};

// ============================================================================
// STOCK INTEGRITY SCORE - LIGHTWEIGHT SUMMARY INDICATOR
// ============================================================================

/**
 * Calculate Stock Integrity Score from existing analytical signals
 * This function ONLY summarizes signals already produced by the system.
 * It does not create new calculations or modify existing logic.
 * 
 * @param {Object} signals - Object containing existing signal outputs
 * @param {string} signals.snapshotFcrStrength - 'Weak' | 'Moderate' | 'Strong' | null
 * @param {string} signals.growthModelStrength - 'Weak' | 'Moderate' | 'Strong' | null
 * @param {number} signals.spreadsheetPersistence - Number of consecutive deviation samples
 * @param {number} signals.sampleReliabilityScore - 0-100 reliability score
 * @returns {Object} Stock Integrity Score result
 */
export const calculateStockIntegrityScore = (signals = {}) => {
  let totalScore = 0;
  const breakdown = [];
  
  // 1) Snapshot Analysis FCR signal strength
  if (signals.snapshotFcrStrength) {
    const strength = signals.snapshotFcrStrength;
    if (strength === 'Weak') {
      totalScore += 1;
      breakdown.push({ source: 'Snapshot FCR', points: 1, label: 'Weak deviation' });
    } else if (strength === 'Moderate') {
      totalScore += 2;
      breakdown.push({ source: 'Snapshot FCR', points: 2, label: 'Moderate deviation' });
    } else if (strength === 'Strong') {
      totalScore += 3;
      breakdown.push({ source: 'Snapshot FCR', points: 3, label: 'Strong deviation' });
    }
  }
  
  // 2) Growth vs Model signal strength
  if (signals.growthModelStrength) {
    const strength = signals.growthModelStrength;
    if (strength === 'Weak') {
      totalScore += 1;
      breakdown.push({ source: 'Growth vs Model', points: 1, label: 'Weak signal' });
    } else if (strength === 'Moderate') {
      totalScore += 2;
      breakdown.push({ source: 'Growth vs Model', points: 2, label: 'Moderate signal' });
    } else if (strength === 'Strong') {
      totalScore += 3;
      breakdown.push({ source: 'Growth vs Model', points: 3, label: 'Strong signal' });
    }
  }
  
  // 3) Spreadsheet Upload deviation persistence
  if (signals.spreadsheetPersistence !== undefined && signals.spreadsheetPersistence !== null) {
    const persistence = signals.spreadsheetPersistence;
    if (persistence >= 4) {
      totalScore += 3;
      breakdown.push({ source: 'Spreadsheet Persistence', points: 3, label: 'Strong persistence' });
    } else if (persistence >= 2) {
      totalScore += 2;
      breakdown.push({ source: 'Spreadsheet Persistence', points: 2, label: 'Moderate persistence' });
    }
  }
  
  // 4) Reliability adjustment
  if (signals.sampleReliabilityScore !== undefined && signals.sampleReliabilityScore < 50) {
    totalScore = Math.max(0, totalScore - 1);
    breakdown.push({ source: 'Reliability Adjustment', points: -1, label: 'Low reliability (score < 50)' });
  }

  // 5) MFI SSOT alignment — if Missing Fish Intelligence detects missing fish,
  //    Stock Integrity MUST NOT show "High Stock Integrity"
  if (signals.mfiDetectionStrength) {
    const ds = signals.mfiDetectionStrength;
    if (ds === 'High' && signals.mfiHasMissingFish) {
      const boost = Math.max(0, 5 - totalScore);
      if (boost > 0) {
        totalScore += boost;
        breakdown.push({ source: 'MFI Detection (High)', points: boost, label: 'Missing fish confirmed' });
      }
    } else if (ds === 'Moderate' && signals.mfiHasMissingFish) {
      const boost = Math.max(0, 3 - totalScore);
      if (boost > 0) {
        totalScore += boost;
        breakdown.push({ source: 'MFI Detection (Moderate)', points: boost, label: 'Missing fish indicated' });
      }
    }
  }
  
  // Determine level
  let level, color;
  if (totalScore <= 2) {
    level = 'High Stock Integrity';
    color = 'green';
  } else if (totalScore <= 4) {
    level = 'Moderate Uncertainty';
    color = 'yellow';
  } else {
    level = 'Potential Stock Discrepancy';
    color = 'red';
  }
  
  return {
    score: totalScore,
    level,
    color,
    breakdown,
  };
};

export { calculateStockIntegrityScore as stockIntegrityScore };
